import React from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from "chart.js";
import { RegionalData } from "../../utils/AnalytivalDataType";
import { Box, useMediaQuery } from "@mui/material";

// Register necessary Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, Title);

interface RegionalChartProps {
  data: RegionalData[];
  jobTitle: string;
}

const RegionalChart: React.FC<RegionalChartProps> = ({ data, jobTitle }) => {
  const percentages = data?.map(({ percentage }) =>
    parseFloat(percentage.replace("%", ""))
  );

  const labelsWithPercentages = data?.map(
    ({ location }) => `${location.display_name}`
  );

  const isMobile = useMediaQuery("(max-width:1200px)");

  // Modern color palette with gradients
  const modernColors = [
    "#6366f1", // Indigo
    "#8b5cf6", // Purple
    "#ec4899", // Pink
    "#f59e0b", // Amber
    "#10b981", // Emerald
    "#3b82f6", // Blue
    "#f97316", // Orange
    "#14b8a6", // Teal
    "#a855f7", // Violet
    "#06b6d4", // Cyan
    "#84cc16", // Lime
    "#f43f5e", // Rose
  ];

  // Prepare chart data
  const chartData = {
    labels: labelsWithPercentages,
    datasets: [
      {
        data: percentages,
        backgroundColor: modernColors,
        borderColor: "#ffffff",
        borderWidth: 3,
        hoverOffset: 15,
        hoverBorderColor: "#ffffff",
        hoverBorderWidth: 4,
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: `${jobTitle} Regional Distribution`,
        font: {
          size: 16,
          weight: "bold" as const,
          family: "'Inter', sans-serif",
        },
        color: "#273848",
        padding: {
          top: 10,
          bottom: 20,
        },
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        titleColor: "#273848",
        bodyColor: "#273848",
        borderColor: "#e0e0e0",
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        titleFont: {
          size: 14,
          weight: "bold" as const,
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: (tooltipItem: any) => {
            const value = tooltipItem.raw;
            const label = tooltipItem.label;
            return ` ${label}: ${value}%`;
          },
        },
      },
      legend: {
        position: isMobile ? ("bottom" as const) : ("right" as const),
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: isMobile ? 10 : 15,
          font: {
            size: isMobile ? 10 : 11,
            family: "'Inter', sans-serif",
          },
          color: "#273848",
          usePointStyle: true,
          pointStyle: "circle",
          generateLabels: (chart: any) => {
            const labels = chart.data.labels || [];
            const dataset = chart.data.datasets[0];
            return labels?.map((label: string, index: number) => {
              const value = dataset.data[index];
              return {
                text: `${label} (${value}%)`,
                fillStyle: dataset.backgroundColor[index],
                strokeStyle: dataset.backgroundColor[index],
                lineWidth: 0,
                hidden: false,
                index: index,
              };
            });
          },
        },
      },
    },
    cutout: "65%",
    animation: {
      animateRotate: true,
      animateScale: true,
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: isMobile ? 350 : 420,
        padding: isMobile ? 2 : 3,
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Doughnut data={chartData} options={options} />
    </Box>
  );
};

export default RegionalChart;
